import { WriteBatch, writeBatch } from 'firebase/firestore';
import { db } from '../lib/firebase';

/**
 * Write prediction data using the pacing established by the team consistency writer:
 * two records per commit and a 1.5-second pause between committed batches.
 * Each commit is awaited so Firestore's existing retries finish before continuing.
 *
 * @param {T[]} entries - Records to write in order.
 * @param {(batch: WriteBatch, entry: T) => void} addEntry - Add one record to the batch.
 *
 * @return {Promise<void>} Resolves after every batch has committed.
 * @throws {Error} Propagates write failures without starting subsequent batches.
 */
export const writePredictionBatches = async <T>(
  entries: T[],
  addEntry: (batch: WriteBatch, entry: T) => void
): Promise<void> => {
  const BATCH_SIZE = 2;
  const BATCH_DELAY_MS = 1500;

  for (let offset = 0; offset < entries.length; offset += BATCH_SIZE) {
    const batch = writeBatch(db);
    entries.slice(offset, offset + BATCH_SIZE).forEach((entry) => addEntry(batch, entry));
    await batch.commit();

    if (offset + BATCH_SIZE < entries.length) {
      await new Promise((resolve) => setTimeout(resolve, BATCH_DELAY_MS));
    }
  }
};
